import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { useGetListHotels } from "../hooks/useGetListHotels";
import { useGetHotelStaticData } from "../hooks/useGetHotelStaticData";
import { Facets, SortByFilter } from "./components/Facets";
import { HotelSearchLoader } from "./components/HotelSearchLoader";
import HotelFilterSkeleton from "./components/HotelFilterSkeleton";
import { HotelListItem } from "./components/HotelListItem";
import HotelListItemSkeleton from "./components/HotelListItemSkeleton";
import { HotelSearchFormik } from "../search/components/HotelSearchFormik";
import { HotelDetailsPopup } from "./components/HotelDetailsPopup";

const ListHotels = () => {
  const { agentActiveCurrency } = useSelector((state) => state.userData);

  const {
    loading,
    mainLoading,
    hotels,
    filter,
    selected,
    setSelected,
    queryString,
    sort,
    originalData,
    setSort,
    allLoading,apiCompleted,header,
    revealMore,
    hasMore,
    loadingMore,
    visibleCount,
  } = useGetListHotels();
  let noOfRooms = filter?.roomInfo?.length
  const ldCity = header?.searchFor || filter?.cityName || "";
  const ldNights = filter?.checkinDate && filter?.checkoutDate
    ? Math.max(1, moment(filter.checkoutDate).diff(moment(filter.checkinDate), "days"))
    : 0;
  const ldAdults = (filter?.roomInfo || []).reduce((a, r) => a + (+r?.noOfAdult || 0), 0);
  const ldChildren = (filter?.roomInfo || []).reduce((a, r) => a + (+r?.noOfChild || 0), 0);
  const [showRoomInfoModal, setShowRoomInfoModal] = useState(false);
  const [hotelDetails, setHotelDetails] = useState({});
  const [isFiltering, setIsFiltering] = useState(false);
  const hasUserScrolledRef = useRef(false);
  const filterTimerRef = useRef(null);
  const hotelListRef = useRef(null);

  // When loadingMore starts, lock the list container's min-height to its current
  // size (hotels + 3 skeletons). This prevents the container from collapsing if
  // skeletons disappear in a render before new hotel cards are added, which causes
  // the page to briefly shrink and the scroll to snap to the footer.
  useLayoutEffect(() => {
    const el = hotelListRef.current;
    if (!el) return;
    if (loadingMore) {
      el.style.minHeight = el.scrollHeight + "px";
    } else {
      el.style.minHeight = "";
    }
  }, [loadingMore]);

  const triggerFilterSkeleton = () => {
    if (loading) return;
    setIsFiltering(true);
    clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => setIsFiltering(false), 400);
  };

  const handleSetSort = (val) => {
    triggerFilterSkeleton();
    setSort(val);
  };

  const handleSetSelected = (val) => {
    triggerFilterSkeleton();
    setSelected(val);
  };

  useEffect(() => () => clearTimeout(filterTimerRef.current), []);

  useEffect(()=>{
    window.scrollTo(0,0)
  },[])

  useEffect(() => {
    if (loading) {
      hasUserScrolledRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 0) {
        hasUserScrolledRef.current = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const divAutoScroll = document.querySelectorAll(
      ".hw--hotel-auto-scroll-div"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const userScrolled = hasUserScrolledRef.current;
            revealMore(userScrolled);
            if (userScrolled) {
              // Consume the gesture — the next page needs a fresh scroll to bottom.
              hasUserScrolledRef.current = false;
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "5px",
        threshold: 0,
      }
    );

    divAutoScroll.forEach((button) => {
      observer.observe(button);
    });

    return () => {
      divAutoScroll.forEach((button) => {
        observer.unobserve(button);
      });
    };
  }, [loading,revealMore]);

  return (
    <div style={{pointerEvents : (loading || mainLoading) ? "none" :""}}>
      <div className="hw-hotes-module hw-hotel--widget hw-hotel--filterWidgetMain">
        <div className="hw-page--width" style={{pointerEvents : (loading || mainLoading) ? "none" :""}}>
          <HotelSearchFormik
            searchUpdate={true}
            showRoomInfoModal={showRoomInfoModal}
            setShowRoomInfoModal={setShowRoomInfoModal}
            queryparams={{
              ...filter,
              cityValueExists: filter.city,
              city: undefined,
              hid: filter.hid,
            }}
            hotelsList={hotels}
            header={header}
          />
        </div>
      </div>
      {loading && (
        <HotelSearchLoader
          city={ldCity}
          inBig={filter?.checkinDate ? moment(filter.checkinDate).format("DD MMM") : ""}
          inDow={filter?.checkinDate ? moment(filter.checkinDate).format("dddd") : ""}
          outBig={filter?.checkoutDate ? moment(filter.checkoutDate).format("DD MMM") : ""}
          outDow={filter?.checkoutDate ? moment(filter.checkoutDate).format("dddd") : ""}
          nights={ldNights}
          rooms={noOfRooms || 0}
          adults={ldAdults}
          children={ldChildren}
        />
      )}
      {(!loading && mainLoading) && (
        <div className="hw-before-loader--status-half hotel" style={{ left: 0 }} />
      )}

      <div className="hw-page--width">
        <div
          className="hw-hotel--list"
          onClick={() => {
            setShowRoomInfoModal(false);
          }}
        >
          <HotelDetailsPopup
            setHotelDetails={setHotelDetails}
            hotelDetails={hotelDetails}
            queryString={queryString}
            searchQuery={filter}
          />
          {loading && (
            <>
              <aside className="hw-hotel--asideFilter" style={{ pointerEvents: "none" }}>
                <HotelFilterSkeleton />
              </aside>
              <div className="hw-hotel--filterResult row-gap--null pb-null">
                <div className="hw-search--flightFilterResult__body">
                  <div className="hw-hotel--filterResult__list">
                    <HotelListItemSkeleton />
                    <HotelListItemSkeleton />
                    <HotelListItemSkeleton />
                    <HotelListItemSkeleton />
                  </div>
                </div>
              </div>
            </>
          )}
          {!loading && (
            <>
              {!(apiCompleted && !mainLoading && (hotels?.length ?? 0) === 0) && (
              <aside className="hw-hotel--asideFilter" style={{ pointerEvents: (loading || mainLoading || isFiltering) ? "none" : "" }}>
                <Facets
                  hotels={hotels}
                  loading={loading}
                  agentActiveCurrency={agentActiveCurrency}
                  selected={selected}
                  setSelected={handleSetSelected}
                  originalData={originalData}
                  sort={sort}
                  setSort={handleSetSort}
                  cityId={filter?.city}
                  queryString={queryString}
                  cityName={header?.searchFor}
                />
              </aside>
              )}
              <div className="hw-hotel--filterResult row-gap--null pb-null">
                {!loading && hotels?.length > 0 && (
                  <div className="hw-hotel--filterResultTop">
                    <SortByFilter setSort={handleSetSort} sort={sort} disable={loading || mainLoading || isFiltering} onClear={() => handleSetSelected({ ...selected, hotelName: "" })} />                  </div>
                )}
                <div className="hw-search--flightFilterResult__body">
                  <>
                    <div className="hw-hotel--filterResult__list" ref={hotelListRef}>
                      {apiCompleted && !loading && !mainLoading && hotels?.length === 0 && (
                        <div className="hw-hotel--ntavailable text-center" style={{ padding: "48px 16px" }}>
                          <svg width="210" height="140" viewBox="0 0 210 140" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "0 auto 20px" }} aria-hidden="true">
                            <g stroke="#b4bac6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
                              <path d="M150 34C145 34 145 25 151 25C151 20 160 18 163 23C167 17 178 20 176 27C182 27 182 34 177 34Z" />
                              <path d="M105 50V38" />
                              <path d="M105 38h11l-3 4 3 4h-11" />
                              <path d="M62 50H148" />
                              <path d="M68 50V112H142V50" />
                              <rect x="76" y="60" width="14" height="13" rx="1.5" />
                              <rect x="98" y="60" width="14" height="13" rx="1.5" />
                              <rect x="120" y="60" width="14" height="13" rx="1.5" />
                              <rect x="76" y="80" width="14" height="13" rx="1.5" />
                              <rect x="98" y="80" width="14" height="13" rx="1.5" />
                              <rect x="120" y="80" width="14" height="13" rx="1.5" />
                              <path d="M98 112V98h14v14" />
                              <path d="M108 104v3" />
                              <path d="M26 112h30" />
                              <path d="M60 112h8" />
                              <path d="M142 112h8" />
                              <path d="M154 112h30" />
                            </g>
                          </svg>
                          <h3 className="hw-black900" style={{ marginBottom: 12 }}>Sorry, No Hotels available</h3>
                          <p className="hw-fontWeight--500" style={{ maxWidth: 460, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>We couldn't find any hotels for your search. Please try a different search.</p>
                          <button type="button" className="hw-btn hw-btn--bprimary" style={{ marginTop: 20, padding: "10px 28px" }} onClick={() => window.history.back()}>
                            Go Back
                          </button>
                        </div>
                      )}

                      {isFiltering ? (
                        <>
                          <HotelListItemSkeleton />
                          <HotelListItemSkeleton />
                          <HotelListItemSkeleton />
                        </>
                      ) : (
                        hotels?.length > 0 && hotels.map((hotel, index) => (
                          <HotelListItem
                            key={hotel?.hotelCode || index}
                            agentActiveCurrency={agentActiveCurrency}
                            hotel={hotel}
                            index={index}
                            queryString={queryString}
                            setHotelDetails={setHotelDetails}
                            searchContext={filter}
                            rooms={noOfRooms || 1}
                            loading={loading}
                            searchCompleted={apiCompleted}
                          />
                        ))
                      )}

                      {loadingMore && (
                        <>
                          <HotelListItemSkeleton />
                          <HotelListItemSkeleton />
                          <HotelListItemSkeleton />
                        </>
                      )}
                    </div>
                    <div className="hw--hotel-auto-scroll-div" />
                  </>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListHotels;
